import { FC, useContext } from "react";

import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { TrainingLinkRequest } from "@/features/training/model/Training/TrainingLink";
import { CustomCloseButton, CustomSaveButton } from "@/features/training/ui";
import CustomInput from "@/shared/forms/CustomInput";
import CustomSelect from "@/shared/forms/CustomSelect";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import {
	sharedTaskDialogActionsSx,
	sharedTaskDialogContentSx,
	sharedTaskDialogHeaderSx,
	sharedTaskDialogPaperSx,
} from "@/styles/sharedTaskDialogStyles";
import CloseIcon from "@mui/icons-material/Close";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import {
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	InputAdornment,
	LinearProgress,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import { useMutateCreateTrainingLink } from "@/service/TrainingMutations";
import { useFetchCourseFolders } from "@/service/TrainingQueries";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


interface NewTrainingLinkProps {
	handleClose: () => void;
	open: boolean;
}

type LinkFormValues = {
	Name: string;
	UrlPath: string;
	FolderId: string | number | null;
};

const validationSchema = Yup.object().shape({
	Name: Yup.string().trim().required("Name is required"),
	UrlPath: Yup.string().trim().required("Url is required"),
});

export const NewTrainingLink: FC<NewTrainingLinkProps> = (props) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const { isLoading, data: folders } = useFetchCourseFolders();
	const createTrainingLink = useMutateCreateTrainingLink();

	const handleSave = async (values: LinkFormValues) => {
		const request: TrainingLinkRequest = {
			Name: values.Name,
			UrlPath: values.UrlPath,
			FolderId:
				values.FolderId === "General" || values.FolderId == null
					? null
					: Number(values.FolderId),
		};

		try {
			await createTrainingLink(request);
			props.handleClose();
			AlertRequest.success();
		} catch (error) {
			console.error(error);
			props.handleClose();
			AlertRequest.failed();
		}
	};

	const closeButtonSx = {
		color: theme.palette.text.secondary,
		"&:hover": {
			color: theme.palette.text.primary,
			backgroundColor: theme.palette.action.hover,
		},
	};

	if (isLoading || folders === undefined) {
		return (
			<Box
				sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
			>
				<LinearProgress />
			</Box>
		);
	}

	return (
		<Dialog
			onClose={props.handleClose}
			open={props.open}
			fullWidth
			maxWidth="sm"
			fullScreen={isMobile}
			aria-labelledby="new-link-title"
			PaperProps={{
				sx: sharedTaskDialogPaperSx(theme, isMobile),
			}}
		>
			<DialogTitle
				id="new-link-title"
				component="div"
				sx={{
					...sharedTaskDialogHeaderSx(theme),
					position: "relative",
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 1.5,
				}}
			>
				<Typography
					variant="caption"
					sx={{
						display: "block",
						fontWeight: 800,
						letterSpacing: "0.08em",
						color: "primary.main",
						mb: 0.4,
					}}
				>
					<Translate String="Auto.Features.Training.Pages.TrainingTab.PanelLeft.Actions.NewTrainingLink.L130C6.TRAINING">TRAINING</Translate>
				</Typography>
				<Typography
					variant="h6"
					component="h2"
					sx={{
						fontWeight: 700,
						color: "text.primary",
						pr: 5,
						mb: 0.75,
					}}
				>
					<Translate String="Training.NewTrainingLink.Title">
						New Link
					</Translate>
				</Typography>
				<Typography
					variant="body2"
					sx={{
						color: "text.secondary",
						mb: 0,
						lineHeight: 1.5,
						pr: 5,
					}}
				>
					<Translate String="Training.NewTrainingLink.Subtitle">
						Create a shortcut to an external resource and optionally place it in
						a folder.
					</Translate>
				</Typography>
				<IconButton
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.TrainingTab.PanelLeft.Actions.NewTrainingLink.L162C17.Close", "Close")}
					onClick={props.handleClose}
					size="small"
					sx={{
						position: "absolute",
						right: 8,
						top: 8,
						...closeButtonSx,
					}}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<DialogContent
				sx={{
					...sharedTaskDialogContentSx(theme),
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 0,
				}}
			>
				<Formik<LinkFormValues>
					initialValues={{
						Name: "",
						UrlPath: "",
						FolderId: "General",
					}}
					validationSchema={validationSchema}
					validateOnMount
					onSubmit={handleSave}
				>
					{({ isValid, dirty, isSubmitting }) => (
						<Form>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={6}>
									<CustomInput
										id="Name"
										type="text"
										label="Name"
										name="Name"
										translationString="Training.NewTrainingLink.Name"
										placeholder="e.g. Safety Handbook"
										autoFocus
										required
										onKeyDown={(e) => e.stopPropagation()}
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<CustomInput
										id="UrlPath"
										type="url"
										label="Link"
										name="UrlPath"
										translationString="Training.NewTrainingLink.Url"
										placeholder="https://example.com/resource"
										required
										onKeyDown={(e) => e.stopPropagation()}
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<LinkOutlinedIcon fontSize="small" />
												</InputAdornment>
											),
										}}
									/>
								</Grid>
								<Grid item xs={12}>
									<CustomSelect
										label="Folder"
										name="FolderId"
										id="FolderId"
										translationString="Training.NewTrainingLink.Folder"
									>
										<option value="General">
											<Translate String="Training.NewTrainingLink.Folder.General">
												General (no folder)
											</Translate>
										</option>
										{folders.map((x) => (
											<option value={x.Id} key={x.Id}>
												{x.Name}
											</option>
										))}
									</CustomSelect>
									<Typography
										variant="caption"
										sx={{
											display: "block",
											mt: 0.5,
											color: "text.secondary",
										}}
									>
										<Translate String="Training.NewTrainingLink.Folder.Help">
											Choose a folder, or keep &quot;General&quot; to store this
											link without a folder.
										</Translate>
									</Typography>
								</Grid>
							</Grid>

							<DialogActions
								sx={{
									...sharedTaskDialogActionsSx(theme, isMobile),
									px: 0,
									pt: 2,
									mt: 3,
									display: "flex",
									justifyContent: "flex-end",
									gap: 1.5,
									flexWrap: "wrap",
								}}
							>
								<CustomCloseButton
									handleClose={props.handleClose}
									disabled={isSubmitting}
								/>
								<CustomSaveButton
									loading={isSubmitting}
									disabled={!isValid || !dirty || isSubmitting}
								/>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};
